import { Button } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckIcon,
  DownloadIcon,
  EyeIcon,
  FileTextIcon,
  HelpCircle,
  MailIcon,
  PrinterIcon,
  SendIcon,
  ShoppingCartIcon,
  XCircleIcon,
} from "lucide-react";
import { quotationInvoiceDocumentTypeDisplayName } from "@/modules/quotation-invoice/domain/document-type";
import type {
  QuotationInvoiceDocumentHistoryEntryDTO,
  QuotationInvoiceDocumentItemDTO,
  QuotationInvoiceDocumentListItem,
  QuotationInvoiceDocumentPaymentDTO,
  LinkedSalesOrderDTO,
} from "@/modules/quotation-invoice/domain/types";
import type { QiStockReservationDTO } from "../api/catalog-types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatDocumentCurrency } from "../lib/format-document-currency";
import { DocumentPaymentsSection } from "./DocumentPaymentsSection";
import { DocumentLineQtyByUnitSummary } from "./DocumentLineQtyByUnitSummary";
import { buildReceiptPaymentRecord } from "./DocumentPdfPreviewContent";
import { DocumentStatusBadge } from "./DocumentStatusBadge";
import { QuotationInvoiceDocumentBody } from "./QuotationInvoiceDocumentBody";

const getDocumentTypeLabel = (type: string, typeName?: string | null) => {
  if (typeName?.trim()) return typeName;
  return quotationInvoiceDocumentTypeDisplayName(type);
};

const getHistoryActionLabel = (action: string) => {
  const map: Record<string, string> = {
    draft_created: "ສ້າງຮ່າງເອກະສານ",
    issued: "ອອກເອກະສານ",
    converted_to_invoice: "ຕໍ່ເປັນໃບບິນ",
    converted_to_sales_order: "ແປງເປັນຄຳສັ່ງຂາຍ",
    converted_to_receipt: "ຕໍ່ເປັນໃບຮັບເງິນ",
    paid: "ຊຳລະເງິນ",
    cancelled: "ຍົກເລີກ",
    payment_recorded: "ບັນທຶກການຊຳລະ",
    payment_updated: "ປັບການຊຳລະ / ສະຖານະຊຳລະ",
    payment_deleted: "ລຶບການຊຳລະ",
  };
  return map[action] ?? action;
};

type DocumentDetailContentProps = {
  document: QuotationInvoiceDocumentListItem;
  items: QuotationInvoiceDocumentItemDTO[];
  history: QuotationInvoiceDocumentHistoryEntryDTO[];
  payments?: QuotationInvoiceDocumentPaymentDTO[];
  /** Invoices linked to this quotation (`quotation_id` = this doc). */
  linkedInvoices?: QuotationInvoiceDocumentListItem[];
  linkedSalesOrder?: LinkedSalesOrderDTO | null;
  stockOrderDetailUrl?: string | null;
  stockReservations?: QiStockReservationDTO[];
  onBack: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
  onEmail?: () => void;
  onConvertToInvoice?: () => void;
  /** ຖ້າມີ — ປຸ່ມອອກໃບບິນງວດຖືກປິດ ແລະ ໃຊ້ເປັນ title tooltip */
  convertToInvoiceDisabledReason?: string | null;
  onConvertToSalesOrder?: () => void;
  convertToSalesOrderPending?: boolean;
  /** ອອກເອກະສານ (draft → issued) — ປຸ່ມສະແດງເມື່ອມີ callback */
  onIssueDocument?: () => void;
  /** ຍົກເລີກເອກະສານ — ປຸ່ມສະແດງເມື່ອມີ callback */
  onCancelDocument?: () => void;
  onStartTour?: () => void;
};

export function DocumentDetailContent({
  document: doc,
  items,
  history,
  payments = [],
  linkedInvoices = [],
  linkedSalesOrder = null,
  stockOrderDetailUrl = null,
  stockReservations = [],
  onBack,
  onDownload,
  onPrint,
  onEmail,
  onConvertToInvoice,
  convertToInvoiceDisabledReason,
  onConvertToSalesOrder,
  convertToSalesOrderPending = false,
  onIssueDocument,
  onCancelDocument,
  onStartTour,
}: DocumentDetailContentProps) {
  const docNumber = doc.documentNumber ?? doc.id.slice(0, 8);
  const showConvertToInvoice =
    doc.documentType === "quotation" &&
    doc.status === "issued" &&
    onConvertToInvoice;
  const showConvertToSalesOrder =
    doc.documentType === "quotation" &&
    doc.status === "issued" &&
    !linkedSalesOrder &&
    onConvertToSalesOrder;
  return (
    <div className="space-y-6">
      {/* Page title + actions */}
      <div
        className="flex flex-wrap items-center justify-between gap-4"
        data-tourid="document-detail-header"
      >
        <div className="flex items-center gap-3">
          <h1 className="font-bold text-2xl tracking-tight">
            {getDocumentTypeLabel(doc.documentType, doc.documentTypeName)} #
            {docNumber}
          </h1>
          {onStartTour && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={onStartTour}
            >
              <HelpCircle className="size-5" />
            </Button>
          )}
          <DocumentStatusBadge status={doc.status} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            ກັບ
          </Button>
          {onDownload && (
            <Button variant="outline" size="sm" onClick={onDownload}>
              <DownloadIcon className="h-4 w-4" />
              ດາວໂຫຼດ PDF
            </Button>
          )}
          {onPrint && (
            <Button variant="default" size="sm" onClick={onPrint}>
              <PrinterIcon className="h-4 w-4" />
              ພິມ
            </Button>
          )}
          {onEmail && (
            <Button variant="outline" size="sm" onClick={onEmail}>
              <MailIcon className="h-4 w-4" />
              ສົ່ງອີເມວ
            </Button>
          )}
          {showConvertToInvoice && (
            <Button
              size="sm"
              variant="secondary"
              disabled={Boolean(convertToInvoiceDisabledReason)}
              title={convertToInvoiceDisabledReason ?? undefined}
              onClick={onConvertToInvoice}
            >
              <FileTextIcon className="h-4 w-4" />
              ອອກໃບບິນງວດຕໍ່ໄປ
            </Button>
          )}
          {showConvertToSalesOrder && (
            <Button
              size="sm"
              variant="secondary"
              disabled={convertToSalesOrderPending}
              onClick={onConvertToSalesOrder}
            >
              <ShoppingCartIcon className="h-4 w-4" />
              ແປງເປັນຄຳສັ່ງຂາຍ
            </Button>
          )}
          {onIssueDocument && (
            <Button
              size="sm"
              variant="default"
              className="gap-2"
              onClick={onIssueDocument}
            >
              <SendIcon className="h-4 w-4" />
              ອອກເອກະສານ
            </Button>
          )}
          {onCancelDocument && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2 border-destructive/50 text-destructive hover:bg-destructive/10"
              onClick={onCancelDocument}
            >
              <XCircleIcon className="h-4 w-4" />
              ຍົກເລີກ
            </Button>
          )}
        </div>
      </div>

      <DocumentLineQtyByUnitSummary items={items} className="rounded-xl border bg-card" />

      {/* Main document card */}
      <div
        className="relative overflow-hidden rounded-xl border bg-card p-6 md:p-8"
        data-tourid="document-detail-info"
      >
        <QuotationInvoiceDocumentBody
          document={doc}
          items={items}
          paymentRecord={buildReceiptPaymentRecord(doc, payments)}
        />
      </div>

      {doc.documentType === "quotation" && stockReservations.length > 0 && (
        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-3 font-semibold">ການຈອງສຕັອກ</h2>
          <ul className="space-y-2 text-sm">
            {stockReservations.map((row) => {
              const item = items.find((line) => line.id === row.documentItemId);
              const label =
                item?.productCode ??
                item?.productName ??
                item?.description ??
                row.productId;
              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-border/60 border-b pb-2 last:border-0 last:pb-0"
                >
                  <span>{label}</span>
                  <div className="flex items-center gap-2 tabular-nums">
                    <span>{Number(row.qty).toLocaleString()}</span>
                    <DocumentStatusBadge
                      status={
                        row.status === "active"
                          ? "issued"
                          : row.status === "fulfilled"
                            ? "paid"
                            : "cancelled"
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {doc.documentType === "quotation" && linkedSalesOrder && stockOrderDetailUrl && (
        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-3 font-semibold">ຄຳສັ່ງຂາຍຈາກລາຄານີ້</h2>
          <a
            href={stockOrderDetailUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary hover:underline"
          >
            {linkedSalesOrder.orderNo}
          </a>
          <span className="ml-2 text-muted-foreground text-sm">
            ({linkedSalesOrder.status})
          </span>
        </div>
      )}

      {doc.documentType === "quotation" && doc.status === "issued" && (
        <div
          className="rounded-xl border bg-card p-6"
          data-tourid="document-linked-invoices"
        >
          <h2 className="mb-3 font-semibold">ໃບບິນຕາມງວດຈາກລາຄານີ້</h2>
          {linkedInvoices.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີໃບບິນ — ໃຊ້ປຸ່ມ &quot;ອອກໃບບິນງວດຕໍ່ໄປ&quot; ເພື່ອສ້າງງວດໃໝ່
              (ແກ້ຍອດງວດໃນຮ່າງກ່ອນອອກເອກະສານ)
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {linkedInvoices.map((inv) => (
                <li
                  key={inv.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-border/60 border-b pb-2 last:border-0 last:pb-0"
                >
                  <Link
                    to="/app/documents/$id"
                    params={{ id: inv.id }}
                    className="font-medium text-primary hover:underline"
                  >
                    {inv.documentNumber?.trim() || inv.id.slice(0, 8)}
                  </Link>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-muted-foreground tabular-nums">
                      {formatDocumentCurrency(inv.total, inv.currency)}
                    </span>
                    <DocumentStatusBadge status={inv.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <DocumentPaymentsSection
        documentId={doc.id}
        documentType={doc.documentType}
        currency={doc.currency}
        documentTotal={doc.total}
        status={doc.status}
        payments={payments}
      />

      {/* Document History */}
      {history.length > 0 && (
        <div
          className="rounded-xl border bg-card p-6"
          data-tourid="document-detail-history"
        >
          <h2 className="mb-4 font-semibold">ປະຫວັດເອກະສານ</h2>
          <ul className="space-y-4">
            {history.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3 text-sm">
                {entry.action === "issued" ? (
                  <SendIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                ) : entry.action === "paid" ? (
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <EyeIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div>
                  <p>
                    {getHistoryActionLabel(entry.action)}
                    {entry.performedByName
                      ? ` ໂດຍ ${entry.performedByName}`
                      : ""}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {formatDateTimeLocal(entry.performedAt)}
                  </p>
                  {entry.notes && (
                    <p className="mt-1 text-muted-foreground">{entry.notes}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
